import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import React, { forwardRef } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { BrandWordmark } from '@/components/ui/BrandWordmark';
import { palette } from '@/config/theme';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

export interface BracketShareData {
  /** Picked champion's display name (already localized by the caller). */
  championName: string;
  /** Picked champion's crest URL — falls back to a trophy glyph. */
  championLogo?: string | null;
  /** Total bracket points scored so far. */
  points: number;
  /** Global bracket-contest rank (1-based); 0/undefined hides the rank chip. */
  rank: number;
}

export const BRACKET_SHARE_CARD_WIDTH = 360;

/**
 * The shareable "my World Cup bracket" card we rasterise to a PNG. Sibling of
 * `BragCard` and follows the SAME capture contract: mounted OFF-SCREEN by the
 * parent, `collapsable={false}`, colours from the STATIC dark `palette` (not the
 * theme hook) so the exported image always reads as the signature Volt card even
 * in light mode. The champion crest is the only network image — a missing crest
 * degrades to a trophy glyph, never a half-loaded logo bleeding into the export.
 *
 * Numbers are formatted via `formatNumber`; the rank string is bidi-isolated the
 * same way the rest of the app handles the RN-web "Text forces LTR" gotcha.
 */
export const BracketShareCard = forwardRef<View, { data: BracketShareData }>(
  function BracketShareCard({ data }, ref) {
    const { t } = useTranslation();
    const { numeralOptions } = useLocale();
    const n = (x: number) => formatNumber(x, numeralOptions);

    const podium = data.rank > 0 && data.rank <= 3;
    const accent = podium ? palette.captain : palette.voltBright;
    const hasRank = data.rank > 0;

    return (
      <View
        ref={ref}
        collapsable={false}
        style={{
          width: BRACKET_SHARE_CARD_WIDTH,
          backgroundColor: palette.deep,
          borderRadius: 28,
          borderWidth: 1,
          borderColor: accent,
          paddingHorizontal: 24,
          paddingTop: 22,
          paddingBottom: 22,
          overflow: 'hidden',
        }}
      >
        {/* Header: wordmark + contest pill */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 22,
          }}
        >
          <BrandWordmark size={20} />
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 5,
              backgroundColor: palette.voltGlass,
              borderColor: palette.voltLine,
              borderWidth: 1,
              borderRadius: 999,
              paddingHorizontal: 10,
              paddingVertical: 4,
            }}
          >
            <Ionicons name="git-network" size={12} color={palette.voltBright} />
            <AppText variant="micro" weight="bold" style={{ color: palette.voltBright }}>
              {t('bracketShare.kicker')}
            </AppText>
          </View>
        </View>

        {/* Hero: champion crest + name */}
        <View
          style={{
            alignItems: 'center',
            backgroundColor: palette.surface2,
            borderRadius: 22,
            borderWidth: 1,
            borderColor: podium ? 'rgba(244,200,75,0.35)' : palette.voltLine,
            paddingVertical: 22,
            paddingHorizontal: 16,
            marginBottom: 16,
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 12 }}>
            <Ionicons name="trophy" size={13} color={accent} />
            <AppText
              variant="micro"
              weight="bold"
              style={{ color: palette.soft, letterSpacing: 1.5 }}
            >
              {t('bracketShare.championLabel')}
            </AppText>
          </View>
          <View
            style={{
              width: 84,
              height: 84,
              borderRadius: 42,
              borderWidth: 2,
              borderColor: accent,
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              backgroundColor: palette.deep,
              marginBottom: 12,
            }}
          >
            {data.championLogo ? (
              <Image
                source={{ uri: data.championLogo }}
                style={{ width: 56, height: 56 }}
                contentFit="contain"
                cachePolicy="memory-disk"
              />
            ) : (
              <Ionicons name="trophy" size={36} color={accent} />
            )}
          </View>
          <AppText
            variant="title"
            weight="bold"
            numberOfLines={1}
            style={{ color: palette.text, textAlign: 'center' }}
          >
            {data.championName}
          </AppText>
        </View>

        {/* Stat strip: points · rank */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'stretch',
            marginBottom: 18,
          }}
        >
          <Stat label={t('bracketShare.points')} value={n(data.points)} />
          {hasRank ? (
            <>
              <Divider />
              <Stat label={t('bracketShare.rank')} value={`#${n(data.rank)}`} highlight={podium} />
            </>
          ) : null}
        </View>

        {/* CTA */}
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <Ionicons name="download-outline" size={14} color={palette.voltBright} />
          <AppText variant="caption" weight="bold" style={{ color: palette.voltBright }}>
            {t('bracketShare.cta')}
          </AppText>
        </View>
      </View>
    );
  },
);

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <View
      style={{
        flex: 1,
        alignItems: 'center',
        gap: 3,
        backgroundColor: palette.surface2,
        borderRadius: 16,
        paddingVertical: 14,
      }}
    >
      <AppText
        weight="bold"
        tabular
        style={{ color: highlight ? palette.captain : palette.voltBright, fontSize: 22 }}
      >
        {value}
      </AppText>
      <AppText
        variant="micro"
        weight="medium"
        style={{ color: palette.muted, letterSpacing: 0.5 }}
      >
        {label}
      </AppText>
    </View>
  );
}

function Divider() {
  return <View style={{ width: 10 }} />;
}
